import { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { api, clearToken } from './api';
import { ToastProvider } from './ui';
import { BRAND_LOGO } from '../data/homeImages';
import { ArrowUpRight, Close, Menu } from '../components/ui/Icons';

type NavLinkItem = { to: string; label: string; exact?: boolean; badge?: 'enquiries' };

const GROUPS: { title: string; links: NavLinkItem[] }[] = [
  {
    title: 'Overview',
    links: [
      { to: '/admin', label: 'Dashboard', exact: true },
      { to: '/admin/enquiries', label: 'Enquiries', badge: 'enquiries' },
    ],
  },
  {
    title: 'Catalogue',
    links: [
      { to: '/admin/products', label: 'Products' },
      { to: '/admin/tds', label: 'TDS documents' },
      { to: '/admin/marmovella', label: 'Marmovella' },
    ],
  },
  {
    title: 'Content',
    links: [
      { to: '/admin/blogs', label: 'Blog' },
      { to: '/admin/banners', label: 'Homepage banners' },
      { to: '/admin/events', label: 'Events' },
      { to: '/admin/guides', label: 'Painting guides' },
      { to: '/admin/services', label: 'Services' },
    ],
  },
  {
    title: 'Colours',
    // The old Colours / Palettes screens and "Advanced content" feed no public page,
    // so they are left out of the menu (their routes still exist).
    links: [{ to: '/admin/color-schemes', label: 'Exterior colour schemes' }],
  },
  {
    title: 'Settings',
    links: [
      { to: '/admin/settings', label: 'Site settings' },
    ],
  },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [newEnquiries, setNewEnquiries] = useState(0);

  useEffect(() => setOpen(false), [location.pathname]);

  // Keep the "new enquiries" badge fresh while the admin is open.
  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .get<{ enquiriesNew: number }>('/api/admin/stats')
        .then((s) => alive && setNewEnquiries(s.enquiriesNew))
        .catch(() => undefined);
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [location.pathname]);

  const isActive = (to: string, exact?: boolean) => (exact ? location.pathname === to : location.pathname === to || location.pathname.startsWith(`${to}/`));

  const logout = () => {
    clearToken();
    navigate('/admin/login');
  };

  const nav = (
    <>
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <Link to="/admin" className="inline-block bg-white rounded-lg px-3 py-2">
          <img src={BRAND_LOGO} alt="Vertex Paints" className="h-7 w-auto" />
        </Link>
        <button type="button" className="lg:hidden text-white/80 p-1" onClick={() => setOpen(false)} aria-label="Close menu">
          <Close size={20} />
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-5">
        {GROUPS.map((g) => (
          <div key={g.title}>
            <p className="px-3 mb-1 text-[10px] font-bold tracking-[.16em] uppercase text-white/40">{g.title}</p>
            <div className="space-y-0.5">
              {g.links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive(l.to, l.exact) ? 'bg-white/10 text-white font-semibold' : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  {l.label}
                  {l.badge === 'enquiries' && newEnquiries > 0 && (
                    <span className="ml-2 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-brand-gold text-brand-ink text-[11px] font-bold flex items-center justify-center">{newEnquiries}</span>
                  )}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-1">
        <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/5 hover:text-white">
          View website <ArrowUpRight size={14} />
        </a>
        <button onClick={logout} className="w-full text-left px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-brand-red/20 hover:text-white">
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <ToastProvider>
      <div className="min-h-screen bg-brand-surface lg:flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-64 flex-shrink-0 bg-brand-navy text-white flex-col sticky top-0 h-screen">{nav}</aside>

        {/* Mobile top bar + drawer */}
        <div className="lg:hidden sticky top-0 z-40 bg-brand-navy text-white flex items-center justify-between px-4 h-14">
          <button type="button" onClick={() => setOpen(true)} className="p-1 -ml-1" aria-label="Open menu">
            <Menu size={22} />
          </button>
          <span className="text-sm font-semibold">Vertex admin</span>
          <span className="w-6">{newEnquiries > 0 && <span className="block w-2.5 h-2.5 rounded-full bg-brand-gold ml-auto" />}</span>
        </div>
        {open && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
            <aside className="relative w-72 max-w-[85vw] bg-brand-navy text-white flex flex-col h-full">{nav}</aside>
          </div>
        )}

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </ToastProvider>
  );
};

export default AdminLayout;
